import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { Button } from '../ui';
import { apiWorker } from '../../api/worker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import toast from 'react-hot-toast';
import { GameLeaderboardScopeTabs } from './common/GameLeaderboardScopeTabs';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Detect if we're on a mobile device (not just narrow viewport). */
function isMobileDevice(): boolean {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/** Detect iOS specifically (no native fullscreen API for divs). */
function isIOSDevice(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** True when viewport is portrait AND on a mobile device. */
function useIsPortraitMobile(): boolean {
  const [isPortrait, setIsPortrait] = useState(false);

  useEffect(() => {
    const check = () => {
      const mobile = isMobileDevice();
      const portrait = window.innerHeight > window.innerWidth;
      setIsPortrait(mobile && portrait);
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  return isPortrait;
}

/**
 * Attempt to lock screen orientation to landscape.
 * Returns true if lock succeeded.
 */
async function tryLockLandscape(): Promise<boolean> {
  try {
    const so = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
    if (so?.lock) {
      await so.lock('landscape');
      return true;
    }
  } catch {
    // lock() threw — browser blocked it or unsupported
  }
  return false;
}

/** Unlock orientation if previously locked. */
function tryUnlockOrientation() {
  try {
    const so = screen.orientation as ScreenOrientation & { unlock?: () => void };
    if (so?.unlock) so.unlock();
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Icons (inline SVG to avoid extra deps)
// ---------------------------------------------------------------------------

function ExpandIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function CollapseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function RotatePhoneIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M12 18h.01" />
      <path d="M2 12 C2 6 6 2 12 2" />
      <polyline points="8 2 12 2 12 6" />
    </svg>
  );
}

function MusicIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  );
}

function MuteIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 5L6 9H2v6h4l5 4V5z" />
      <line x1="23" y1="9" x2="17" y2="15" />
      <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SubmarineGame() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const [gameState, setGameState] = useState<'start' | 'playing' | 'gameover'>('start');
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [isExpertMode, setIsExpertMode] = useState(false);
  const [instructionsVisible, setInstructionsVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  /** CSS-only fake fullscreen for iOS / browsers without Fullscreen API */
  const [isFakeFullscreen, setIsFakeFullscreen] = useState(false);
  /** True when device is stuck portrait but we want landscape — triggers CSS rotation */
  const [forceRotate, setForceRotate] = useState(false);

  const { isSignedIn, user } = useCurrentUser();
  const { node, isGlobal } = useTenant();
  const hasActiveNode = Boolean(node && !isGlobal && node.id !== 'platform-root');
  const nodeName = hasActiveNode ? (node?.name || 'Organization') : '';
  const [leaderboardScope, setLeaderboardScope] = useState<'world' | 'node'>('world');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [playerName, setPlayerName] = useState(() => user?.username || user?.fullName || user?.firstName || '');
  const [scoreSubmitted, setScoreSubmitted] = useState(false);

  // Fill playerName from the auth user once it's available (render-phase adjustment;
  // setState during render with a previous-value guard is the sanctioned pattern).
  const [playerNameUser, setPlayerNameUser] = useState(user);
  if (user && !playerName && user !== playerNameUser) {
    setPlayerNameUser(user);
    setPlayerName(user.username || user.fullName || user.firstName || '');
  }

  const { data: leaderboardsData, isLoading: isLeaderboardLoading } = useQuery<any>({
    queryKey: ['deep-sea-explorer-high-scores', leaderboardScope, leaderboardScope === 'node' ? node?.id : 'world'],
    queryFn: async () => {
      try {
        const params: any = {};
        if (leaderboardScope === 'node' && hasActiveNode && node?.id) {
          params.nodeId = node.id;
        }
        const res = await apiWorker.get('/api/tools/deep-sea-explorer/high-scores', { params, silent: true });
        return res.data;
      } catch (err) {
        console.warn('Failed to fetch deep-sea-explorer high scores:', err);
        return [];
      }
    }
  });

  const leaderboards: Array<{ playerName: string; score: number; depth?: string; date?: string }> = 
    Array.isArray(leaderboardsData)
      ? leaderboardsData
      : Array.isArray(leaderboardsData?.content)
        ? leaderboardsData.content
        : Array.isArray(leaderboardsData?.data)
          ? leaderboardsData.data
          : [];

  const submitScoreMutation = useMutation({
    mutationFn: async (scoreData: { playerName: string; score: number }) => {
      return await apiWorker.post('/api/tools/deep-sea-explorer/high-scores', {
        playerName: scoreData.playerName,
        score: scoreData.score,
        game: 'deep-sea-explorer',
        nodeId: hasActiveNode ? node?.id : undefined,
        metadata: {
          distance: scoreData.score,
          depth: `${scoreData.score}m`
        }
      }, { silent: true });
    },
    onSuccess: (res: any) => {
      setScoreSubmitted(true);
      const isNewHigh = res?.data?.isNewHighScore;
      toast.success(isNewHigh ? '🏆 New High Score Recorded!' : 'High score recorded!');
      queryClient.invalidateQueries({ queryKey: ['deep-sea-explorer-high-scores'] });
      setShowLeaderboard(true);
    },
    onError: (err: any) => {
      if (err?.response?.status === 401) {
        toast.error('Sign in required to save score to global leaderboard.', { id: 'sub-401' });
      } else {
        toast.error('Could not connect to leaderboard server.');
      }
    }
  });

  const isPortraitMobile = useIsPortraitMobile();
  
  // Game Constants
  const BASE_WIDTH = 800;
  const BASE_HEIGHT = 500;
  const ASPECT = BASE_WIDTH / BASE_HEIGHT; // 1.6
  const GRAVITY = 0.3;
  const THRUST = -0.6;
  const MAX_VELOCITY = 5;
  const OBSTACLE_SPEED = 3;
  const OBSTACLE_WIDTH = 60;
  const OBSTACLE_SPACING = 400;
  const PEARL_RADIUS = 10;
  const SUB_WIDTH = 60;
  const SUB_HEIGHT = 30;

  // Audio Context Ref
  const audioCtxRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);

  // State Refs for animation loop
  const stateRef = useRef({
    subY: 200,
    subV: 0,
    isThrusting: false,
    obstacles: [] as { x: number, topHeight: number, bottomHeight: number, passed: boolean }[],
    pearls: [] as { x: number, y: number, collected: boolean }[],
    particles: [] as { x: number, y: number, speed: number, size: number }[],
    score: 0,
    frame: 0,
    lastObstacleX: 0,
    gameOverTriggered: false,
    distraction: { type: null as 'squid' | 'jellyfish' | 'sonar' | 'whale' | null, timer: 0, data: {} as any }
  });

  const reqRef = useRef<number>(0);

  // -----------------------------------------------------------------------
  // Fullscreen (with iOS / restricted-browser fallbacks)
  // -----------------------------------------------------------------------

  const effectiveFullscreen = isFullscreen || isFakeFullscreen;

  const toggleFullscreen = useCallback(async () => {
    const el = wrapperRef.current;
    if (!el) return;

    // --- EXIT ---
    if (isFullscreen || isFakeFullscreen) {
      // Real fullscreen exit
      const doc = document as Document & { webkitExitFullscreen?: () => Promise<void>; webkitFullscreenElement?: Element };
      if (document.fullscreenElement) {
        await document.exitFullscreen().catch(() => {});
      } else if (doc.webkitFullscreenElement && doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      }
      // Fake fullscreen exit
      setIsFakeFullscreen(false);
      setForceRotate(false);
      tryUnlockOrientation();
      setIsFullscreen(false);
      return;
    }

    // --- ENTER ---
    let nativeWorked = false;

    // 1. Try standard Fullscreen API
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen();
        nativeWorked = true;
      }
    } catch { /* blocked */ }

    // 2. Try webkit prefix (older Safari / WebView)
    if (!nativeWorked) {
      try {
        const webkitEl = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
        if (webkitEl.webkitRequestFullscreen) {
          await webkitEl.webkitRequestFullscreen();
          nativeWorked = true;
        }
      } catch { /* blocked */ }
    }

    // 3. Try orientation lock to landscape
    const locked = await tryLockLandscape();

    // 4. If native fullscreen failed → CSS fake fullscreen
    if (!nativeWorked) {
      setIsFakeFullscreen(true);
      setIsFullscreen(true);

      // If portrait and orientation lock failed → CSS rotation
      if (!locked && window.innerHeight > window.innerWidth) {
        setForceRotate(true);
      }
    }
  }, [isFullscreen, isFakeFullscreen]);

  // Sync isFullscreen with native fullscreen events
  useEffect(() => {
    const onChange = () => {
      const doc = document as Document & { webkitFullscreenElement?: Element };
      const inFS = !!(document.fullscreenElement || doc.webkitFullscreenElement);
      setIsFullscreen(inFS || isFakeFullscreen);
      if (!inFS && !isFakeFullscreen) {
        setForceRotate(false);
        tryUnlockOrientation();
      }
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, [isFakeFullscreen]);

  // When orientation changes while in fake fullscreen, update forceRotate
  useEffect(() => {
    if (!isFakeFullscreen) return;
    const check = () => {
      const portrait = window.innerHeight > window.innerWidth;
      setForceRotate(portrait);
    };
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, [isFakeFullscreen]);

  // Inject viewport meta tag for iOS to prevent pinch-zoom on the game
  useEffect(() => {
    let meta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    const original = meta?.content || '';
    if (effectiveFullscreen && isMobileDevice()) {
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'viewport';
        document.head.appendChild(meta);
      }
      meta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
    }
    return () => {
      if (meta && original) meta.content = original;
    };
  }, [effectiveFullscreen]);

  // -----------------------------------------------------------------------
  // Dynamic canvas sizing — fill container while keeping 16:10 ratio
  // -----------------------------------------------------------------------

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    let w = rect.width;
    let h = rect.height;

    // Maintain aspect ratio
    if (w / h > ASPECT) {
      w = h * ASPECT;
    } else {
      h = w / ASPECT;
    }

    // Use integer device pixels for crisp rendering
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);

    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);

    // CSS size
    canvas.style.width = `${Math.round(w)}px`;
    canvas.style.height = `${Math.round(h)}px`;
  }, [ASPECT]);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas, isFullscreen]);

  // -----------------------------------------------------------------------
  // Audio Initialization & Synthesis
  // -----------------------------------------------------------------------

  const initAudio = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
  };

  const playThrustSound = () => {
    initAudio();
    if (!audioCtxRef.current) return;
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.1);
    
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  };

  const playCrashSound = () => {
    initAudio();
    if (!audioCtxRef.current) return;
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    
    // Noise burst
    const bufferSize = ctx.sampleRate * 0.5; // 0.5 seconds
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = buffer;
    
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1000, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.5);
    
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.5, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    
    noiseSource.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    
    noiseSource.start();
  };

  const playPearlSound = () => {
    initAudio();
    if (!audioCtxRef.current) return;
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
    
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  };

  // -----------------------------------------------------------------------
  // Background Music Synthesizer (Randomize / NY Groove / Sea Shanty / Kids / Take A Chance / Knowing Me)
  // -----------------------------------------------------------------------
  const musicTimerRef = useRef<number | null>(null);
  const musicGainRef = useRef<GainNode | null>(null);
  const musicNodesRef = useRef<AudioNode[]>([]);
  type BaseMusicTrack = 'ny_groove' | 'sea_shanty' | 'mgmt_kids' | 'take_a_chance' | 'knowing_me';
  type MusicTrack = 'random' | BaseMusicTrack | 'off';
  
  const ALL_TRACKS: BaseMusicTrack[] = ['ny_groove', 'sea_shanty', 'mgmt_kids', 'take_a_chance', 'knowing_me'];
  const TRACK_TITLES: Record<BaseMusicTrack, string> = {
    ny_groove: 'NY GROOVE',
    sea_shanty: 'SEA SHANTY',
    mgmt_kids: 'MGMT KIDS',
    take_a_chance: 'TAKE A CHANCE',
    knowing_me: 'KNOWING ME'
  };

  const [musicTrack, setMusicTrack] = useState<MusicTrack>('random');
  const [currentRandomTrack, setCurrentRandomTrack] = useState<BaseMusicTrack>('ny_groove');
  const currentRandomTrackRef = useRef<BaseMusicTrack>('ny_groove');

  const stopBackgroundMusic = useCallback(() => {
    if (musicTimerRef.current !== null) {
      window.clearInterval(musicTimerRef.current);
      musicTimerRef.current = null;
    }
    if (oscillatorRef.current) {
      try { oscillatorRef.current.stop(); } catch { /* already stopped */ }
      oscillatorRef.current = null;
    }
    // Disconnect all audio nodes from the previous session to kill delay tails
    if (musicGainRef.current) {
      try { musicGainRef.current.disconnect(); } catch { /* already disconnected */ }
      musicGainRef.current = null;
    }
    for (const node of musicNodesRef.current) {
      try { node.disconnect(); } catch { /* already disconnected */ }
    }
    musicNodesRef.current = [];
  }, []);

  const startBackgroundMusic = useCallback(() => {
    initAudio();
    if (!audioCtxRef.current) return;
    stopBackgroundMusic();
    if (musicTrack === 'off') return;

    const ctx = audioCtxRef.current;

    // Pick active song
    let activeTrack: BaseMusicTrack;
    if (musicTrack === 'random') {
      const pool = ALL_TRACKS.filter(t => t !== currentRandomTrackRef.current);
      const chosen = pool[Math.floor(Math.random() * pool.length)] || ALL_TRACKS[0];
      currentRandomTrackRef.current = chosen;
      setCurrentRandomTrack(chosen);
      activeTrack = chosen;
    } else {
      activeTrack = musicTrack;
      setCurrentRandomTrack(musicTrack);
      currentRandomTrackRef.current = musicTrack;
    }
    
    // Notes table (Hz) — full chromatic C1–B6
    const NOTE_FREQ: Record<string, number> = {
      C1: 32.70, CSH1: 34.65, D1: 36.71, DSH1: 38.89, E1: 41.20, F1: 43.65, FSH1: 46.25, G1: 49.00, GSH1: 51.91, A1: 55.00, ASH1: 58.27, B1: 61.74,
      C2: 65.41, CSH2: 69.30, D2: 73.42, DSH2: 77.78, E2: 82.41, F2: 87.31, FSH2: 92.50, G2: 98.00, GSH2: 103.83, A2: 110.00, ASH2: 116.54, B2: 123.47,
      C3: 130.81, CSH3: 138.59, D3: 146.83, DSH3: 155.56, E3: 164.81, F3: 174.61, FSH3: 185.00, G3: 196.00, GSH3: 207.65, A3: 220.00, ASH3: 233.08, B3: 246.94,
      C4: 261.63, CSH4: 277.18, D4: 293.66, DSH4: 311.13, E4: 329.63, F4: 349.23, FSH4: 369.99, G4: 392.00, GSH4: 415.30, A4: 440.00, ASH4: 466.16, B4: 493.88,
      C5: 523.25, CSH5: 554.37, D5: 587.33, DSH5: 622.25, E5: 659.25, F5: 698.46, FSH5: 739.99, G5: 783.99, GSH5: 830.61, A5: 880.00, ASH5: 932.33, B5: 987.77,
      C6: 1046.50, CSH6: 1108.73, D6: 1174.66, DSH6: 1244.51, E6: 1318.51, F6: 1396.91, FSH6: 1479.98, G6: 1567.98, GSH6: 1661.22, A6: 1760.00, ASH6: 1864.66, B6: 1975.53,
    };

    // 1. Ace Frehley "New York Groove"
    const NY_BASS_A = [
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2',
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'B2', 'REST', 'B2', 'B2', 'REST', 'B2', 'B2', 'REST',
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2',
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2',
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'B2', 'REST', 'B2', 'B2', 'REST', 'B2', 'B2', 'REST',
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2',
      // Chorus Bass
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2',
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'B2', 'REST', 'B2', 'B2', 'REST', 'B2', 'B2', 'REST',
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2',
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2',
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'B2', 'REST', 'B2', 'B2', 'REST', 'B2', 'B2', 'REST',
      'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'REST', 'E2', 'E2', 'A2', 'A2', 'B2', 'B2'
    ];
    const NY_MELODY_A = [
      'E4', 'REST', 'GSH4', 'REST', 'B4', 'REST', 'B4', 'B4', 'REST', 'A4', 'GSH4', 'FSH4', 'E4', 'REST', 'REST', 'REST',
      'B4', 'REST', 'B4', 'REST', 'CSH5', 'REST', 'B4', 'REST', 'GSH4', 'REST', 'FSH4', 'REST', 'E4', 'REST', 'REST', 'REST',
      'B4', 'B4', 'REST', 'CSH5', 'REST', 'B4', 'REST', 'GSH4', 'A4', 'B4', 'REST', 'E5', 'REST', 'B4', 'GSH4', 'E4',
      'GSH4', 'A4', 'B4', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST', 'E4', 'GSH4', 'B4', 'E5', 'REST', 'REST', 'REST',
      'E4', 'REST', 'GSH4', 'REST', 'B4', 'REST', 'B4', 'B4', 'REST', 'A4', 'GSH4', 'FSH4', 'E4', 'REST', 'REST', 'REST',
      'B4', 'REST', 'B4', 'REST', 'CSH5', 'REST', 'B4', 'REST', 'GSH4', 'REST', 'FSH4', 'REST', 'E4', 'REST', 'REST', 'REST',
      'B4', 'B4', 'REST', 'CSH5', 'REST', 'B4', 'REST', 'GSH4', 'A4', 'B4', 'REST', 'E5', 'REST', 'B4', 'GSH4', 'E4',
      'GSH4', 'A4', 'B4', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST', 'E4', 'GSH4', 'B4', 'E5', 'REST', 'REST', 'REST',
      // Chorus Melody
      'B4', 'REST', 'E5', 'REST', 'REST', 'REST', 'REST', 'REST', 'B4', 'REST', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST',
      'B4', 'REST', 'E5', 'REST', 'REST', 'REST', 'REST', 'REST', 'B4', 'REST', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST',
      'B4', 'REST', 'E5', 'REST', 'REST', 'REST', 'REST', 'REST', 'B4', 'REST', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST',
      'B4', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST', 'REST', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST', 'REST', 'REST',
      'B4', 'REST', 'E5', 'REST', 'REST', 'REST', 'REST', 'REST', 'B4', 'REST', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST',
      'B4', 'REST', 'E5', 'REST', 'REST', 'REST', 'REST', 'REST', 'B4', 'REST', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST',
      'B4', 'REST', 'E5', 'REST', 'REST', 'REST', 'REST', 'REST', 'B4', 'REST', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST',
      'B4', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST', 'REST', 'CSH5', 'B4', 'GSH4', 'FSH4', 'E4', 'REST', 'REST', 'REST'
    ];

    // 2. Sea Shanty ("Wellerman")
    const SHANTY_BASS_A = [
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'REST', 'A2', 'REST', 'A2', 'REST',
      'C3', 'REST', 'C3', 'C3', 'REST', 'C3', 'C3', 'REST', 'G2', 'REST', 'G2', 'G2', 'REST', 'G2', 'G2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'REST', 'A2', 'REST', 'A2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'REST', 'A2', 'REST', 'A2', 'REST',
      'C3', 'REST', 'C3', 'C3', 'REST', 'C3', 'C3', 'REST', 'G2', 'REST', 'G2', 'G2', 'REST', 'G2', 'G2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'REST', 'A2', 'REST', 'A2', 'REST'
    ];
    const SHANTY_MELODY_A = [
      'A3', 'REST', 'A3', 'REST', 'A3', 'C4', 'E4', 'REST', 'E4', 'REST', 'E4', 'D4', 'C4', 'REST', 'B3', 'REST',
      'A3', 'REST', 'A3', 'REST', 'A3', 'C4', 'E4', 'REST', 'E4', 'REST', 'D4', 'C4', 'B3', 'REST', 'A3', 'REST',
      'C4', 'REST', 'C4', 'REST', 'E4', 'REST', 'G4', 'REST', 'G4', 'REST', 'F4', 'E4', 'D4', 'REST', 'E4', 'REST',
      'A3', 'REST', 'A3', 'REST', 'C4', 'REST', 'E4', 'REST', 'E4', 'REST', 'D4', 'C4', 'B3', 'REST', 'A3', 'REST',
      'A3', 'REST', 'A3', 'REST', 'A3', 'C4', 'E4', 'REST', 'E4', 'REST', 'E4', 'D4', 'C4', 'REST', 'B3', 'REST',
      'A3', 'REST', 'A3', 'REST', 'A3', 'C4', 'E4', 'REST', 'E4', 'REST', 'D4', 'C4', 'B3', 'REST', 'A3', 'REST',
      'C4', 'REST', 'C4', 'REST', 'E4', 'REST', 'G4', 'REST', 'G4', 'REST', 'F4', 'E4', 'D4', 'REST', 'E4', 'REST',
      'A3', 'REST', 'A3', 'REST', 'C4', 'REST', 'E4', 'REST', 'E4', 'REST', 'D4', 'C4', 'B3', 'REST', 'A3', 'REST'
    ];

    // 3. MGMT Kids
    const KIDS_BASS_A = [
      'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'D2', 'REST', 'D2', 'D2', 'REST', 'D2', 'D2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST',
      'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'D2', 'REST', 'D2', 'D2', 'REST', 'D2', 'D2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST',
      'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'D2', 'REST', 'D2', 'D2', 'REST', 'D2', 'D2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST',
      'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'D2', 'REST', 'D2', 'D2', 'REST', 'D2', 'D2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST',
      // Verse
      'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'D2', 'REST', 'D2', 'D2', 'REST', 'D2', 'D2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST',
      'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'D2', 'REST', 'D2', 'D2', 'REST', 'D2', 'D2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST',
      'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'D2', 'REST', 'D2', 'D2', 'REST', 'D2', 'D2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST',
      'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'FSH2', 'FSH2', 'REST', 'D2', 'REST', 'D2', 'D2', 'REST', 'D2', 'D2', 'REST',
      'A2', 'REST', 'A2', 'A2', 'REST', 'A2', 'A2', 'REST', 'E2', 'REST', 'E2', 'E2', 'REST', 'E2', 'E2', 'REST'
    ];
    const KIDS_MELODY_A = [
      'CSH5', 'REST', 'E5', 'REST', 'CSH5', 'REST', 'E5', 'REST', 'FSH5', 'REST', 'GSH5', 'REST', 'FSH5', 'REST', 'E5', 'REST',
      'CSH5', 'REST', 'E5', 'REST', 'CSH5', 'REST', 'E5', 'REST', 'FSH5', 'REST', 'GSH5', 'REST', 'FSH5', 'REST', 'E5', 'REST',
      'CSH5', 'REST', 'E5', 'REST', 'CSH5', 'REST', 'E5', 'REST', 'FSH5', 'REST', 'GSH5', 'REST', 'FSH5', 'REST', 'E5', 'REST',
      'B4', 'REST', 'B4', 'REST', 'B4', 'REST', 'B4', 'REST', 'CSH5', 'REST', 'CSH5', 'REST', 'B4', 'REST', 'A4', 'REST',
      'CSH5', 'REST', 'E5', 'REST', 'CSH5', 'REST', 'E5', 'REST', 'FSH5', 'REST', 'GSH5', 'REST', 'FSH5', 'REST', 'E5', 'REST',
      'CSH5', 'REST', 'E5', 'REST', 'CSH5', 'REST', 'E5', 'REST', 'FSH5', 'REST', 'GSH5', 'REST', 'FSH5', 'REST', 'E5', 'REST',
      'CSH5', 'REST', 'E5', 'REST', 'CSH5', 'REST', 'E5', 'REST', 'FSH5', 'REST', 'GSH5', 'REST', 'FSH5', 'REST', 'E5', 'REST',
      'B4', 'REST', 'B4', 'REST', 'B4', 'REST', 'B4', 'REST', 'CSH5', 'REST', 'CSH5', 'REST', 'B4', 'REST', 'A4', 'REST',
      // Verse
      'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST',
      'REST', 'REST', 'REST', 'REST', 'FSH4', 'REST', 'GSH4', 'REST', 'A4', 'REST', 'B4', 'REST', 'CSH5', 'REST', 'B4', 'REST',
      'A4', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST'
    ];

    // 4. ABBA "Take a Chance on Me" (Authentic B Major Nu-Disco Arrangement)
    const TAC_BASS_A = [
      // Intro (Bars 1-4) - Pumping Disco Bass Groove
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'DSH3', 'FSH3', 'GSH3',
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'DSH3', 'FSH3', 'GSH3',
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'DSH3', 'FSH3', 'ASH3',
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'DSH3', 'FSH3',
      // Chorus (Bars 5-12) - "If you change your mind..."
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'DSH3', 'FSH3',
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'DSH3', 'FSH3', 'GSH3',
      'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'ASH2', 'CSH3',
      'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'ASH2', 'CSH3', 'DSH3',
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'GSH2', 'REST', 'GSH2', 'GSH3', 'GSH2', 'REST', 'B2', 'DSH3',
      'E2', 'REST', 'E2', 'E3', 'E2', 'REST', 'E2', 'E3', 'E2', 'REST', 'E2', 'E3', 'E2', 'GSH2', 'B2', 'CSH3',
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'DSH3', 'FSH3',
      'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'ASH2', 'CSH3', 'DSH3', 'B2', 'REST', 'FSH2', 'B2',
      // Post-Chorus Hook (Bars 13-16) - "Take a chance on me!"
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'DSH3', 'FSH3',
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'DSH3', 'FSH3', 'GSH3',
      'E2', 'REST', 'E2', 'E3', 'E2', 'REST', 'E2', 'E3', 'E2', 'REST', 'E2', 'E3', 'E2', 'GSH2', 'B2', 'CSH3',
      'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'ASH2', 'CSH3', 'DSH3',
      // Verse (Bars 17-24) - "We can go dancing, we can go walking..."
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'DSH3', 'FSH3',
      'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'REST', 'B2', 'B3', 'B2', 'DSH3', 'FSH3', 'GSH3',
      'CSH2', 'REST', 'CSH2', 'CSH3', 'CSH2', 'REST', 'CSH2', 'CSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'ASH2', 'CSH3',
      'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'ASH2', 'CSH3', 'DSH3',
      'GSH2', 'REST', 'GSH2', 'GSH3', 'GSH2', 'REST', 'GSH2', 'GSH3', 'GSH2', 'REST', 'GSH2', 'GSH3', 'GSH2', 'B2', 'DSH3', 'FSH3',
      'E2', 'REST', 'E2', 'E3', 'E2', 'REST', 'E2', 'E3', 'CSH2', 'REST', 'CSH2', 'CSH3', 'CSH2', 'E2', 'GSH2', 'B2',
      'GSH2', 'REST', 'GSH2', 'GSH3', 'GSH2', 'REST', 'GSH2', 'GSH3', 'E2', 'REST', 'E2', 'E3', 'E2', 'GSH2', 'B2', 'DSH3',
      'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'FSH2', 'FSH2', 'FSH2', 'FSH2', 'ASH2', 'CSH3', 'DSH3'
    ];
    const TAC_MELODY_A = [
      // Intro (Bars 1-4) - "Take a chance, take a chance, take a take a chance chance"
      'B4', 'DSH5', 'FSH5', 'REST', 'B4', 'DSH5', 'FSH5', 'REST', 'B4', 'DSH5', 'B4', 'DSH5', 'FSH5', 'REST', 'FSH5', 'REST',
      'B4', 'DSH5', 'FSH5', 'REST', 'B4', 'DSH5', 'FSH5', 'REST', 'B4', 'DSH5', 'B4', 'DSH5', 'FSH5', 'REST', 'FSH5', 'REST',
      'B4', 'DSH5', 'FSH5', 'REST', 'B4', 'DSH5', 'FSH5', 'REST', 'DSH5', 'FSH5', 'GSH5', 'ASH5', 'B5', 'REST', 'B5', 'REST',
      'B4', 'DSH5', 'FSH5', 'REST', 'B4', 'DSH5', 'FSH5', 'REST', 'FSH5', 'REST', 'DSH5', 'REST', 'B4', 'REST', 'FSH4', 'GSH4',
      // Chorus (Bars 5-12)
      'B4', 'REST', 'B4', 'CSH5', 'DSH5', 'REST', 'DSH5', 'REST', 'DSH5', 'CSH5', 'B4', 'GSH4', 'FSH4', 'REST', 'REST', 'REST',
      'FSH4', 'GSH4', 'B4', 'CSH5', 'DSH5', 'REST', 'DSH5', 'REST', 'DSH5', 'CSH5', 'B4', 'GSH4', 'B4', 'REST', 'REST', 'REST',
      'FSH4', 'GSH4', 'CSH5', 'REST', 'CSH5', 'DSH5', 'CSH5', 'REST', 'CSH5', 'B4', 'ASH4', 'GSH4', 'FSH4', 'REST', 'REST', 'REST',
      'FSH4', 'GSH4', 'CSH5', 'REST', 'CSH5', 'DSH5', 'CSH5', 'REST', 'CSH5', 'B4', 'ASH4', 'GSH4', 'FSH4', 'REST', 'REST', 'REST',
      'DSH5', 'DSH5', 'DSH5', 'CSH5', 'B4', 'REST', 'DSH5', 'DSH5', 'DSH5', 'CSH5', 'B4', 'CSH5', 'DSH5', 'REST', 'DSH5', 'REST',
      'E5', 'REST', 'E5', 'DSH5', 'CSH5', 'REST', 'CSH5', 'DSH5', 'E5', 'DSH5', 'CSH5', 'B4', 'GSH4', 'REST', 'B4', 'REST',
      'DSH5', 'DSH5', 'DSH5', 'CSH5', 'B4', 'REST', 'DSH5', 'DSH5', 'DSH5', 'CSH5', 'B4', 'CSH5', 'DSH5', 'REST', 'DSH5', 'REST',
      'CSH5', 'CSH5', 'CSH5', 'B4', 'ASH4', 'REST', 'CSH5', 'DSH5', 'E5', 'DSH5', 'CSH5', 'B4', 'B4', 'REST', 'REST', 'REST',
      // Post-Chorus Hook (Bars 13-16) - "Take a chance on me!"
      'FSH5', 'REST', 'DSH5', 'REST', 'B4', 'REST', 'REST', 'REST', 'B4', 'DSH5', 'FSH5', 'REST', 'B4', 'DSH5', 'FSH5', 'REST',
      'FSH5', 'REST', 'DSH5', 'REST', 'B4', 'REST', 'REST', 'REST', 'DSH5', 'FSH5', 'GSH5', 'ASH5', 'B5', 'REST', 'FSH5', 'REST',
      'GSH5', 'REST', 'E5', 'REST', 'CSH5', 'REST', 'B4', 'REST', 'CSH5', 'DSH5', 'E5', 'DSH5', 'CSH5', 'B4', 'GSH4', 'REST',
      'FSH5', 'REST', 'DSH5', 'REST', 'CSH5', 'REST', 'B4', 'REST', 'CSH5', 'DSH5', 'CSH5', 'B4', 'ASH4', 'REST', 'FSH4', 'GSH4',
      // Verse (Bars 17-24) - "We can go dancing, we can go walking..."
      'B4', 'REST', 'B4', 'B4', 'B4', 'REST', 'B4', 'REST', 'B4', 'REST', 'B4', 'B4', 'B4', 'REST', 'B4', 'REST',
      'B4', 'REST', 'B4', 'CSH5', 'DSH5', 'REST', 'DSH5', 'CSH5', 'B4', 'REST', 'GSH4', 'REST', 'FSH4', 'REST', 'REST', 'REST',
      'CSH5', 'REST', 'CSH5', 'CSH5', 'CSH5', 'REST', 'CSH5', 'REST', 'CSH5', 'REST', 'CSH5', 'CSH5', 'CSH5', 'REST', 'CSH5', 'REST',
      'CSH5', 'REST', 'CSH5', 'DSH5', 'E5', 'REST', 'E5', 'DSH5', 'CSH5', 'REST', 'ASH4', 'REST', 'FSH4', 'REST', 'REST', 'REST',
      'DSH5', 'REST', 'DSH5', 'DSH5', 'DSH5', 'REST', 'DSH5', 'DSH5', 'E5', 'REST', 'DSH5', 'CSH5', 'B4', 'REST', 'REST', 'REST',
      'E5', 'REST', 'E5', 'E5', 'E5', 'REST', 'E5', 'E5', 'FSH5', 'REST', 'E5', 'DSH5', 'CSH5', 'REST', 'REST', 'REST',
      'DSH5', 'REST', 'DSH5', 'DSH5', 'DSH5', 'REST', 'DSH5', 'DSH5', 'E5', 'REST', 'DSH5', 'CSH5', 'B4', 'REST', 'REST', 'REST',
      'CSH5', 'REST', 'DSH5', 'REST', 'E5', 'REST', 'FSH5', 'REST', 'FSH5', 'FSH5', 'FSH5', 'FSH5', 'FSH5', 'REST', 'FSH4', 'GSH4'
    ];

    // 5. ABBA "Knowing Me, Knowing You" (Authentic D Major Arrangement)
    const KM_BASS_A = [
      // Intro Riff (Bars 1-4) - D -> G -> A -> D
      'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'FSH2', 'A2', 'CSH3',
      'G2', 'REST', 'G2', 'G3', 'G2', 'REST', 'G2', 'G3', 'G2', 'REST', 'G2', 'G3', 'G2', 'B2', 'D3', 'G3',
      'A2', 'REST', 'A2', 'A3', 'A2', 'REST', 'A2', 'A3', 'A2', 'REST', 'A2', 'A3', 'A2', 'CSH3', 'E3', 'G3',
      'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'A2', 'FSH2', 'E2', 'D2', 'REST', 'A2', 'D3',
      // Verse 1 (Bars 5-12) - "No more carefree laughter..."
      'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'FSH2', 'A2', 'D3',
      'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'A2', 'CSH3', 'FSH3',
      'G2', 'REST', 'G2', 'G3', 'G2', 'REST', 'G2', 'G3', 'G2', 'REST', 'G2', 'G3', 'G2', 'B2', 'D3', 'G3',
      'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'FSH2', 'A2', 'D3',
      'E2', 'REST', 'E2', 'E3', 'E2', 'REST', 'E2', 'E3', 'E2', 'REST', 'E2', 'E3', 'E2', 'G2', 'B2', 'E3',
      'A2', 'REST', 'A2', 'A3', 'A2', 'REST', 'A2', 'A3', 'A2', 'REST', 'A2', 'A3', 'A2', 'CSH3', 'E3', 'G3',
      'E2', 'REST', 'E2', 'E3', 'E2', 'REST', 'E2', 'E3', 'E2', 'REST', 'E2', 'E3', 'E2', 'G2', 'B2', 'E3',
      'A2', 'REST', 'A2', 'A3', 'A2', 'REST', 'A2', 'A3', 'A2', 'B2', 'CSH3', 'E3', 'A2', 'CSH3', 'E3', 'A3',
      // Chorus (Bars 13-20) - "Knowing me, knowing you (ah-haa)..."
      'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'FSH2', 'A2', 'CSH3',
      'G2', 'REST', 'G2', 'G3', 'G2', 'REST', 'G2', 'G3', 'G2', 'REST', 'G2', 'G3', 'G2', 'B2', 'D3', 'G3',
      'A2', 'REST', 'A2', 'A3', 'A2', 'REST', 'A2', 'A3', 'A2', 'REST', 'A2', 'A3', 'A2', 'CSH3', 'E3', 'G3',
      'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'FSH2', 'A2', 'B2', 'CSH3', 'D3', 'E3', 'FSH3',
      'G2', 'REST', 'G2', 'G3', 'G2', 'REST', 'G2', 'G3', 'G2', 'REST', 'G2', 'G3', 'G2', 'B2', 'D3', 'G3',
      'A2', 'REST', 'A2', 'A3', 'A2', 'REST', 'A2', 'A3', 'B2', 'REST', 'B2', 'B3', 'B2', 'D3', 'FSH3', 'B3',
      'G2', 'REST', 'G2', 'G3', 'G2', 'REST', 'G2', 'G3', 'FSH2', 'REST', 'FSH2', 'FSH3', 'FSH2', 'A2', 'D3', 'FSH3',
      'E2', 'REST', 'E2', 'E3', 'E2', 'G2', 'B2', 'E3', 'A2', 'REST', 'A2', 'A3', 'D2', 'REST', 'A2', 'D2',
      // Post-Chorus Riff (Bars 21-24)
      'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'FSH2', 'A2', 'CSH3',
      'G2', 'REST', 'G2', 'G3', 'G2', 'REST', 'G2', 'G3', 'G2', 'REST', 'G2', 'G3', 'G2', 'B2', 'D3', 'G3',
      'A2', 'REST', 'A2', 'A3', 'A2', 'REST', 'A2', 'A3', 'A2', 'REST', 'A2', 'A3', 'A2', 'CSH3', 'E3', 'G3',
      'D2', 'REST', 'D2', 'D3', 'D2', 'REST', 'D2', 'D3', 'D2', 'A2', 'FSH2', 'E2', 'D2', 'REST', 'A2', 'D3'
    ];
    const KM_MELODY_A = [
      // Intro Riff (Bars 1-4) - Signature 12-string guitar arpeggio hook
      'D4', 'FSH4', 'A4', 'D5', 'CSH5', 'B4', 'A4', 'FSH4', 'D4', 'FSH4', 'A4', 'D5', 'CSH5', 'B4', 'A4', 'FSH4',
      'G4', 'B4', 'D5', 'G5', 'FSH5', 'E5', 'D5', 'B4', 'G4', 'B4', 'D5', 'G5', 'FSH5', 'E5', 'D5', 'B4',
      'A4', 'CSH5', 'E5', 'A5', 'G5', 'FSH5', 'E5', 'CSH5', 'A4', 'CSH5', 'E5', 'G5', 'FSH5', 'E5', 'D5', 'CSH5',
      'D4', 'FSH4', 'A4', 'D5', 'CSH5', 'B4', 'A4', 'FSH4', 'D5', 'REST', 'D5', 'REST', 'D5', 'REST', 'REST', 'REST',
      // Verse 1 (Bars 5-12) - "No more carefree laughter..."
      'REST', 'REST', 'D4', 'REST', 'D4', 'REST', 'D4', 'E4', 'FSH4', 'REST', 'FSH4', 'REST', 'FSH4', 'REST', 'REST', 'REST',
      'REST', 'REST', 'FSH4', 'REST', 'FSH4', 'REST', 'FSH4', 'G4', 'A4', 'REST', 'A4', 'REST', 'A4', 'REST', 'REST', 'REST',
      'B4', 'REST', 'B4', 'REST', 'B4', 'B4', 'B4', 'A4', 'G4', 'REST', 'FSH4', 'REST', 'E4', 'REST', 'REST', 'REST',
      'FSH4', 'REST', 'FSH4', 'REST', 'E4', 'REST', 'D4', 'REST', 'D4', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST',
      'G4', 'REST', 'G4', 'REST', 'G4', 'G4', 'G4', 'FSH4', 'E4', 'REST', 'D4', 'REST', 'CSH4', 'REST', 'REST', 'REST',
      'E4', 'REST', 'E4', 'REST', 'D4', 'REST', 'CSH4', 'REST', 'D4', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST',
      'REST', 'REST', 'E4', 'REST', 'FSH4', 'REST', 'G4', 'REST', 'A4', 'REST', 'B4', 'REST', 'CSH5', 'REST', 'D5', 'REST',
      'E5', 'REST', 'D5', 'REST', 'CSH5', 'REST', 'B4', 'REST', 'A4', 'REST', 'G4', 'REST', 'FSH4', 'REST', 'E4', 'REST',
      // Chorus (Bars 13-20) - "Knowing me, knowing you (ah-haa)..."
      'A4', 'REST', 'FSH4', 'REST', 'D4', 'REST', 'REST', 'REST', 'A4', 'REST', 'G4', 'REST', 'FSH4', 'REST', 'G4', 'REST',
      'E5', 'REST', 'E5', 'REST', 'D5', 'REST', 'D5', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST',
      'A4', 'REST', 'A4', 'B4', 'CSH5', 'REST', 'B4', 'REST', 'A4', 'REST', 'G4', 'REST', 'FSH4', 'REST', 'REST', 'REST',
      'A4', 'REST', 'FSH4', 'REST', 'D4', 'REST', 'REST', 'REST', 'A4', 'REST', 'G4', 'REST', 'FSH4', 'REST', 'G4', 'REST',
      'E5', 'REST', 'E5', 'REST', 'D5', 'REST', 'D5', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST', 'REST',
      'A4', 'A4', 'B4', 'CSH5', 'D5', 'REST', 'CSH5', 'REST', 'B4', 'REST', 'B4', 'CSH5', 'D5', 'REST', 'REST', 'REST',
      'FSH5', 'FSH5', 'FSH5', 'E5', 'D5', 'CSH5', 'D5', 'E5', 'FSH5', 'REST', 'E5', 'D5', 'CSH5', 'B4', 'A4', 'REST',
      'B4', 'A4', 'G4', 'FSH4', 'E4', 'REST', 'D4', 'REST', 'G4', 'FSH4', 'E4', 'CSH4', 'D4', 'REST', 'REST', 'REST',
      // Post-Chorus Riff (Bars 21-24)
      'D4', 'FSH4', 'A4', 'D5', 'CSH5', 'B4', 'A4', 'FSH4', 'D4', 'FSH4', 'A4', 'D5', 'CSH5', 'B4', 'A4', 'FSH4',
      'G4', 'B4', 'D5', 'G5', 'FSH5', 'E5', 'D5', 'B4', 'G4', 'B4', 'D5', 'G5', 'FSH5', 'E5', 'D5', 'B4',
      'A4', 'CSH5', 'E5', 'A5', 'G5', 'FSH5', 'E5', 'CSH5', 'A4', 'CSH5', 'E5', 'G5', 'FSH5', 'E5', 'D5', 'CSH5',
      'D4', 'FSH4', 'A4', 'D5', 'CSH5', 'B4', 'A4', 'FSH4', 'D5', 'REST', 'D5', 'REST', 'D5', 'REST', 'REST', 'REST'
    ];

    // Composition helpers
    const transposeOctave = (note: string) => {
      if (note === 'REST') return 'REST';
      const m = note.match(/([A-Z]+)(\d)/);
      if (!m) return note;
      return `${m[1]}${parseInt(m[2]) + 1}`;
    };

    const CHROMATIC = ['C', 'CSH', 'D', 'DSH', 'E', 'F', 'FSH', 'G', 'GSH', 'A', 'ASH', 'B'];
    const tSemi = (semi: number) => (note: string): string => {
      if (note === 'REST') return 'REST';
      const m = note.match(/([A-Z]+)(\d)/);
      if (!m) return note;
      const idx = CHROMATIC.indexOf(m[1]);
      if (idx === -1) return note;
      const raw = idx + semi;
      const newOct = parseInt(m[2]) + Math.floor(raw / 12);
      const newName = CHROMATIC[((raw % 12) + 12) % 12];
      return `${newName}${newOct}`;
    };

    const bars = (arr: string[], start: number, count: number) =>
      arr.slice(start * 16, (start + count) * 16);
    const rest = (barCount: number): string[] => Array(barCount * 16).fill('REST');

    const nyV1b = bars(NY_BASS_A, 0, 4),  nyV1m = bars(NY_MELODY_A, 0, 4);
    const nyV2b = bars(NY_BASS_A, 4, 4),  nyV2m = bars(NY_MELODY_A, 4, 4);
    const nyC1b = bars(NY_BASS_A, 8, 4),  nyC1m = bars(NY_MELODY_A, 8, 4);
    const nyC2b = bars(NY_BASS_A, 12, 4), nyC2m = bars(NY_MELODY_A, 12, 4);
    const up5 = tSemi(5);
    const up7 = tSemi(7);
    const NY_BASS = [
      ...nyV1b, ...nyV1b, ...nyV2b, ...nyC1b, ...nyC2b,
      ...nyV1b.map(up5), ...nyV2b.map(up5), ...nyC1b, ...nyC2b,
      ...nyV2b.map(up7), ...nyC1b.map(up7), ...nyC1b, ...nyC2b,
      ...nyC1b.map(transposeOctave), ...nyC2b, ...nyV1b,
    ];
    const NY_MELODY = [
      ...rest(4), ...nyV1m, ...nyV2m, ...nyC1m, ...nyC2m,
      ...nyV1m.map(up5), ...nyV2m.map(up5), ...nyC1m, ...nyC2m,
      ...nyV2m.map(up7), ...nyC1m.map(up7), ...nyC1m, ...nyC2m,
      ...nyC1m.map(transposeOctave), ...nyC2m, ...nyV1m,
    ];

    const shVb = bars(SHANTY_BASS_A, 0, 4),  shVm = bars(SHANTY_MELODY_A, 0, 4);
    const shCb = bars(SHANTY_BASS_A, 4, 4),  shCm = bars(SHANTY_MELODY_A, 4, 4);
    const up2 = tSemi(2);
    const SHANTY_HALF_B = [
      ...shVb, ...shVb, ...shCb, ...shVb, ...shCb,
      ...shVb, ...shCb, ...shVb.map(up2), ...shCb.map(up2), ...shCb, ...shVb,
    ];
    const SHANTY_HALF_M = [
      ...rest(4), ...shVm, ...shCm, ...shVm, ...shCm,
      ...shVm, ...shCm, ...shVm.map(up2), ...shCm.map(up2), ...shCm, ...shVm,
    ];
    const SHANTY_BASS = [...SHANTY_HALF_B, ...SHANTY_HALF_B.map(up5)];
    const SHANTY_MELODY = [...SHANTY_HALF_M, ...SHANTY_HALF_M.map(transposeOctave)];

    const kdS1b = bars(KIDS_BASS_A, 0, 4),  kdS1m = bars(KIDS_MELODY_A, 0, 4);
    const kdS2b = bars(KIDS_BASS_A, 4, 4),  kdS2m = bars(KIDS_MELODY_A, 4, 4);
    const kdV1b = bars(KIDS_BASS_A, 8, 4),  kdV1m = bars(KIDS_MELODY_A, 8, 4);
    const kdV2b = bars(KIDS_BASS_A, 12, 4), kdV2m = bars(KIDS_MELODY_A, 12, 4);
    const dn3 = tSemi(-3);
    const KIDS_HALF_B = [
      ...kdS1b, ...kdS2b, ...kdV1b, ...kdV2b, ...kdS1b, ...kdS2b,
      ...kdV1b.map(dn3), ...kdV2b.map(dn3), ...kdS1b, ...kdS2b,
      ...kdV1b.map(up5), ...kdS1b.map(up5), ...kdS1b, ...kdS2b, ...kdV1b, ...kdS1b,
    ];
    const KIDS_HALF_M = [
      ...kdS1m, ...kdS2m, ...kdV1m, ...kdV2m, ...kdS1m, ...kdS2m,
      ...kdV1m.map(dn3), ...kdV2m.map(dn3), ...kdS1m, ...kdS2m,
      ...kdV1m.map(up5), ...kdS1m.map(up5), ...kdS1m, ...kdS2m, ...kdV1m, ...kdS1m,
    ];
    const KIDS_BASS = [...KIDS_HALF_B, ...KIDS_HALF_B.map(transposeOctave)];
    const KIDS_MELODY = [...KIDS_HALF_M, ...KIDS_HALF_M.map(transposeOctave)];

    const tcIntroB = bars(TAC_BASS_A, 0, 4),   tcIntroM = bars(TAC_MELODY_A, 0, 4);
    const tcChorusB = bars(TAC_BASS_A, 4, 8),  tcChorusM = bars(TAC_MELODY_A, 4, 8);
    const tcPostB = bars(TAC_BASS_A, 12, 4),   tcPostM = bars(TAC_MELODY_A, 12, 4);
    const tcVerseB = bars(TAC_BASS_A, 16, 8),  tcVerseM = bars(TAC_MELODY_A, 16, 8);
    const TAC_BASS = [
      ...tcIntroB,
      ...tcChorusB,
      ...tcPostB,
      ...tcVerseB,
      ...tcChorusB,
      ...tcPostB,
      ...tcIntroB
    ];
    const TAC_MELODY = [
      ...tcIntroM,
      ...tcChorusM,
      ...tcPostM,
      ...tcVerseM,
      ...tcChorusM,
      ...tcPostM,
      ...tcIntroM
    ];

    const kmIntroB = bars(KM_BASS_A, 0, 4),   kmIntroM = bars(KM_MELODY_A, 0, 4);
    const kmVerseB = bars(KM_BASS_A, 4, 8),   kmVerseM = bars(KM_MELODY_A, 4, 8);
    const kmChorusB = bars(KM_BASS_A, 12, 8), kmChorusM = bars(KM_MELODY_A, 12, 8);
    const kmPostB = bars(KM_BASS_A, 20, 4),   kmPostM = bars(KM_MELODY_A, 20, 4);

    const KM_BASS = [
      ...kmIntroB,
      ...kmVerseB,
      ...kmChorusB,
      ...kmPostB,
      ...kmVerseB,
      ...kmChorusB,
      ...kmPostB
    ];
    const KM_MELODY = [
      ...kmIntroM,
      ...kmVerseM,
      ...kmChorusM,
      ...kmPostM,
      ...kmVerseM,
      ...kmChorusM,
      ...kmPostM
    ];

    const isShanty = activeTrack === 'sea_shanty';
    const isKids = activeTrack === 'mgmt_kids';
    const isTAC = activeTrack === 'take_a_chance';
    const isKM = activeTrack === 'knowing_me';
    const BASS_LINE = isKM ? KM_BASS : isTAC ? TAC_BASS : isKids ? KIDS_BASS : (isShanty ? SHANTY_BASS : NY_BASS);
    const MELODY_HOOK = isKM ? KM_MELODY : isTAC ? TAC_MELODY : isKids ? KIDS_MELODY : (isShanty ? SHANTY_MELODY : NY_MELODY);
    const stepTime = isKM ? 0.140 : isTAC ? 0.134 : isKids ? 0.114 : (isShanty ? 0.124 : 0.118);
    let step = 0;

    // ------------------------------------------------------------------
    // Effects Chain: Master Bus + Studio Dynamics Compressor + Lush Space
    // ------------------------------------------------------------------
    const masterCompressor = ctx.createDynamicsCompressor();
    masterCompressor.threshold.setValueAtTime(-16, ctx.currentTime);
    masterCompressor.knee.setValueAtTime(8, ctx.currentTime);
    masterCompressor.ratio.setValueAtTime(12, ctx.currentTime);
    masterCompressor.attack.setValueAtTime(0.003, ctx.currentTime);
    masterCompressor.release.setValueAtTime(0.12, ctx.currentTime);

    const masterFilter = ctx.createBiquadFilter();
    masterFilter.type = 'lowpass';
    masterFilter.frequency.setValueAtTime(8800, ctx.currentTime);

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.68, ctx.currentTime);

    // Lush Stereo Reverb
    const length = Math.floor(ctx.sampleRate * 1.2);
    const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const data = impulse.getChannelData(c);
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.8);
      }
    }
    const reverb = ctx.createConvolver();
    reverb.buffer = impulse;
    const reverbGain = ctx.createGain();
    reverbGain.gain.setValueAtTime(0.22, ctx.currentTime);

    // Audio Graph Connections
    masterFilter.connect(masterCompressor);
    masterFilter.connect(reverb);
    reverb.connect(reverbGain);
    reverbGain.connect(masterCompressor);

    masterCompressor.connect(masterGain);
    masterGain.connect(ctx.destination);
    
    musicGainRef.current = masterGain;
    musicNodesRef.current = [masterFilter, masterCompressor, reverb, reverbGain, masterGain];

    // ------------------------------------------------------------------
    // Punchy Modern Drum Generators (Hitting Hard)
    // ------------------------------------------------------------------
    const triggerKick = (time: number, punch = 1.0) => {
      if (!audioCtxRef.current) return;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(175, time);
      osc.frequency.exponentialRampToValueAtTime(36, time + 0.08);
      g.gain.setValueAtTime(0.95 * punch, time);
      g.gain.exponentialRampToValueAtTime(0.01, time + 0.14);
      osc.connect(g);
      g.connect(masterCompressor);
      osc.start(time);
      osc.stop(time + 0.14);

      const click = ctx.createOscillator();
      const clickGain = ctx.createGain();
      click.type = 'triangle';
      click.frequency.setValueAtTime(350, time);
      click.frequency.exponentialRampToValueAtTime(60, time + 0.015);
      clickGain.gain.setValueAtTime(0.45 * punch, time);
      clickGain.gain.exponentialRampToValueAtTime(0.01, time + 0.015);
      click.connect(clickGain);
      clickGain.connect(masterCompressor);
      click.start(time);
      click.stop(time + 0.015);
    };

    const triggerSnareOrClap = (time: number, isClap = false) => {
      if (!audioCtxRef.current) return;
      const bufSize = Math.floor(ctx.sampleRate * 0.14);
      const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufSize * 0.35));
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buf;

      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.setValueAtTime(isClap ? 1400 : 1900, time);
      band.Q.setValueAtTime(isClap ? 1.4 : 1.8, time);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(isClap ? 0.38 : 0.45, time);
      gain.gain.exponentialRampToValueAtTime(0.01, time + 0.12);

      noise.connect(band);
      band.connect(gain);
      gain.connect(masterCompressor);
      noise.start(time);

      if (!isClap) {
        const tone = ctx.createOscillator();
        const toneGain = ctx.createGain();
        tone.type = 'triangle';
        tone.frequency.setValueAtTime(220, time);
        tone.frequency.exponentialRampToValueAtTime(110, time + 0.06);
        toneGain.gain.setValueAtTime(0.35, time);
        toneGain.gain.exponentialRampToValueAtTime(0.01, time + 0.06);
        tone.connect(toneGain);
        toneGain.connect(masterCompressor);
        tone.start(time);
        tone.stop(time + 0.06);
      } else {
        const pBufSize = Math.floor(ctx.sampleRate * 0.015);
        const pBuf = ctx.createBuffer(1, pBufSize, ctx.sampleRate);
        const pData = pBuf.getChannelData(0);
        for (let i = 0; i < pBufSize; i++) pData[i] = (Math.random() * 2 - 1);
        const pNoise = ctx.createBufferSource();
        pNoise.buffer = pBuf;
        const pGain = ctx.createGain();
        pGain.gain.setValueAtTime(0.2, time);
        pNoise.connect(band);
        pNoise.start(Math.max(ctx.currentTime, time - 0.012));
      }
    };

    const triggerHiHat = (time: number, isOpen = false) => {
      if (!audioCtxRef.current) return;
      const dur = isOpen ? 0.18 : 0.04;
      const bufSize = Math.floor(ctx.sampleRate * dur);
      const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufSize * (isOpen ? 0.45 : 0.3)));
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buf;

      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.setValueAtTime(7500, time);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(isOpen ? 0.28 : 0.18, time);
      gain.gain.exponentialRampToValueAtTime(0.01, time + dur);

      noise.connect(hp);
      hp.connect(gain);
      gain.connect(masterCompressor);
      noise.start(time);
    };

    let nextNoteTime = ctx.currentTime + 0.05;

    const scheduleNote = (time: number, stepIdx: number) => {
      if (!audioCtxRef.current) return;

      const bNote = BASS_LINE[stepIdx];
      const mNote = MELODY_HOOK[stepIdx];

      // ------------------------------------------------------------------
      // Rhythm / Beat Programming Per Song
      // ------------------------------------------------------------------
      if (activeTrack === 'ny_groove') {
        // Nu-Disco / Glam Beat
        if (stepIdx % 4 === 0) triggerKick(time, 1.0);
        if (stepIdx % 8 === 4) triggerSnareOrClap(time, true);
        if (stepIdx % 2 === 0) triggerHiHat(time, stepIdx % 8 === 6);
      } else if (activeTrack === 'sea_shanty') {
        // Deep Sea Club / Slap House Beat
        if (stepIdx % 4 === 0 || stepIdx % 16 === 14) triggerKick(time, stepIdx % 4 === 0 ? 1.0 : 0.7);
        if (stepIdx % 8 === 4) triggerSnareOrClap(time, false);
        if (stepIdx % 4 === 2) triggerHiHat(time, true); // offbeat open hat
      } else if (activeTrack === 'mgmt_kids') {
        // Electro Indie-Dance Beat
        if (stepIdx % 4 === 0 || stepIdx % 16 === 10) triggerKick(time, 1.0);
        if (stepIdx % 8 === 4) triggerSnareOrClap(time, false);
        if (stepIdx % 2 === 0) triggerHiHat(time, stepIdx % 8 === 6);
      } else if (activeTrack === 'take_a_chance') {
        // Pumping Nu-Disco Synthwave Beat
        if (stepIdx % 4 === 0) triggerKick(time, 1.0);
        if (stepIdx % 8 === 4) triggerSnareOrClap(time, true);
        if (stepIdx % 2 === 0) triggerHiHat(time, stepIdx % 4 === 2);
        if (stepIdx % 16 === 14 || stepIdx % 16 === 15) triggerHiHat(time, false);
      } else if (activeTrack === 'knowing_me') {
        // Retrowave Anthem Beat
        if (stepIdx % 16 === 0 || stepIdx % 16 === 6 || stepIdx % 16 === 8 || stepIdx % 16 === 14) {
          triggerKick(time, 0.95);
        }
        if (stepIdx % 8 === 4) triggerSnareOrClap(time, false);
        if (stepIdx % 2 === 0) triggerHiHat(time, stepIdx % 8 === 2);
      }

      // ------------------------------------------------------------------
      // Heavy Modern Bass (Sub-Bass + Mid Grunt + Sidechain Ducking)
      // ------------------------------------------------------------------
      if (bNote !== 'REST' && NOTE_FREQ[bNote]) {
        const freq = NOTE_FREQ[bNote];
        const isKickHit = stepIdx % 4 === 0;

        // Sub Bass (Sine)
        const subOsc = ctx.createOscillator();
        const subGain = ctx.createGain();
        subOsc.type = 'sine';
        const subFreq = freq > 100 ? freq / 2 : freq;
        subOsc.frequency.setValueAtTime(subFreq, time);

        const baseSubVol = 0.55;
        if (isKickHit) {
          subGain.gain.setValueAtTime(baseSubVol * 0.3, time);
          subGain.gain.linearRampToValueAtTime(baseSubVol, time + 0.045);
        } else {
          subGain.gain.setValueAtTime(0.01, time);
          subGain.gain.linearRampToValueAtTime(baseSubVol, time + 0.01);
        }
        subGain.gain.exponentialRampToValueAtTime(baseSubVol * 0.4, time + stepTime * 1.2);
        subGain.gain.linearRampToValueAtTime(0.001, time + stepTime * 1.8);

        subOsc.connect(subGain);
        subGain.connect(masterCompressor);
        subOsc.start(time);
        subOsc.stop(time + stepTime * 1.8);

        // Mid Bass (Dual Saw/Square with Resonant Filter Sweep)
        const midOsc1 = ctx.createOscillator();
        const midOsc2 = ctx.createOscillator();
        midOsc1.type = 'sawtooth';
        midOsc2.type = 'square';
        midOsc1.frequency.setValueAtTime(freq, time);
        midOsc2.frequency.setValueAtTime(freq * 1.004, time);

        const midFilter = ctx.createBiquadFilter();
        midFilter.type = 'lowpass';
        midFilter.Q.setValueAtTime(3.5, time);
        midFilter.frequency.setValueAtTime(2600, time);
        midFilter.frequency.exponentialRampToValueAtTime(240, time + stepTime * 1.2);

        const midGain = ctx.createGain();
        const baseMidVol = 0.32;
        if (isKickHit) {
          midGain.gain.setValueAtTime(baseMidVol * 0.35, time);
          midGain.gain.linearRampToValueAtTime(baseMidVol, time + 0.04);
        } else {
          midGain.gain.setValueAtTime(0.01, time);
          midGain.gain.linearRampToValueAtTime(baseMidVol, time + 0.01);
        }
        midGain.gain.exponentialRampToValueAtTime(0.05, time + stepTime * 1.4);
        midGain.gain.linearRampToValueAtTime(0.001, time + stepTime * 1.8);

        midOsc1.connect(midFilter);
        midOsc2.connect(midFilter);
        midFilter.connect(midGain);
        midGain.connect(masterFilter);

        midOsc1.start(time);
        midOsc2.start(time);
        midOsc1.stop(time + stepTime * 1.8);
        midOsc2.stop(time + stepTime * 1.8);
      }

      // ------------------------------------------------------------------
      // Lead Melody (Stereo Supersaw Synth)
      // ------------------------------------------------------------------
      if (mNote !== 'REST' && NOTE_FREQ[mNote]) {
        const freq = NOTE_FREQ[mNote];
        const g = ctx.createGain();
        
        g.gain.setValueAtTime(0.01, time);
        g.gain.linearRampToValueAtTime(0.28, time + 0.015);
        g.gain.exponentialRampToValueAtTime(0.18, time + stepTime * 0.9);
        g.gain.linearRampToValueAtTime(0.001, time + stepTime * 1.5);

        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.Q.setValueAtTime(2.0, time);
        filter.frequency.setValueAtTime(7500, time);
        filter.frequency.exponentialRampToValueAtTime(1200, time + stepTime * 1.2);

        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const osc3 = ctx.createOscillator();

        osc1.type = 'sawtooth';
        osc2.type = 'sawtooth';
        osc3.type = 'square';

        osc1.frequency.setValueAtTime(freq, time);
        osc2.frequency.setValueAtTime(freq * 1.009, time);
        osc3.frequency.setValueAtTime(freq * 0.991, time);

        if (ctx.createStereoPanner) {
          const pan1 = ctx.createStereoPanner();
          const pan2 = ctx.createStereoPanner();
          const pan3 = ctx.createStereoPanner();
          pan1.pan.setValueAtTime(0, time);
          pan2.pan.setValueAtTime(-0.65, time);
          pan3.pan.setValueAtTime(0.65, time);

          osc1.connect(pan1); osc2.connect(pan2); osc3.connect(pan3);
          pan1.connect(filter); pan2.connect(filter); pan3.connect(filter);
        } else {
          osc1.connect(filter); osc2.connect(filter); osc3.connect(filter);
        }

        filter.connect(g);
        g.connect(masterFilter);

        osc1.start(time); osc2.start(time); osc3.start(time);
        osc1.stop(time + stepTime * 1.5);
        osc2.stop(time + stepTime * 1.5);
        osc3.stop(time + stepTime * 1.5);
      }
    };

    const scheduler = () => {
      if (!audioCtxRef.current) return;
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      
      const currentTime = audioCtxRef.current.currentTime;
      
      if (nextNoteTime < currentTime - 0.2) {
        nextNoteTime = currentTime + 0.05;
      }
      
      while (nextNoteTime < currentTime + 0.15) {
        const stepIdx = step % BASS_LINE.length;
        scheduleNote(nextNoteTime, stepIdx);
        step++;
        nextNoteTime += stepTime;
      }
    };

    scheduler();
    musicTimerRef.current = window.setInterval(scheduler, 50);
  }, [musicTrack, stopBackgroundMusic]);

  useEffect(() => {
    if (gameState === 'playing') {
      if (musicTrack !== 'off') {
        startBackgroundMusic();
      } else {
        stopBackgroundMusic();
      }
    }
    return () => {
      stopBackgroundMusic();
    };
  }, [musicTrack, gameState, startBackgroundMusic, stopBackgroundMusic]);

  useEffect(() => {
    return () => {
      stopBackgroundMusic();
    };
  }, [stopBackgroundMusic]);

  // -----------------------------------------------------------------------
  // Canvas logical dimensions (used by game logic, not raw canvas.width)
  // -----------------------------------------------------------------------
  const startGame = () => {
    initAudio();
    if (musicTrack === 'random') {
      const pool = ALL_TRACKS.filter(t => t !== currentRandomTrackRef.current);
      const chosen = pool[Math.floor(Math.random() * pool.length)] || ALL_TRACKS[0];
      currentRandomTrackRef.current = chosen;
      setCurrentRandomTrack(chosen);
    }
    setGameState('playing');
    setScore(0);
    setScoreSubmitted(false);
    
    const w = BASE_WIDTH;
    const h = BASE_HEIGHT;

    stateRef.current = {
      subY: h / 2,
      subV: 0,
      isThrusting: false,
      obstacles: [],
      pearls: [],
      particles: Array.from({ length: 50 }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        speed: Math.random() * 2 + 0.5,
        size: Math.random() * 3 + 1
      })),
      score: 0,
      frame: 0,
      lastObstacleX: w / 2,
      gameOverTriggered: false,
      distraction: { type: null, timer: 0, data: {} }
    };
  };

  const gameOver = () => {
    if (stateRef.current.gameOverTriggered) return;
    stateRef.current.gameOverTriggered = true;
    
    const finalScore = stateRef.current.score;
    setGameState('gameover');
    setHighScore(prev => Math.max(prev, finalScore));
    playCrashSound();

    // Persist score locally on device for guest/offline gameplay
    try {
      const customSaved = localStorage.getItem('deep_sea_explorer_scores');
      const parsed = customSaved ? JSON.parse(customSaved) : [];
      const list = Array.isArray(parsed) ? parsed : [];
      const name = playerName.trim() || (isSignedIn ? (user?.username || user?.fullName || 'Captain') : 'Guest');
      list.push({
        playerName: name,
        score: finalScore,
        depth: `${finalScore}m`,
        date: new Date().toISOString().split('T')[0]
      });
      list.sort((a: any, b: any) => b.score - a.score);
      localStorage.setItem('deep_sea_explorer_scores', JSON.stringify(list.slice(0, 20)));
    } catch {
      // Best-effort leaderboard save; ignore storage failures.
    }
  };

  const drawSubmarine = (ctx: CanvasRenderingContext2D, x: number, y: number, isThrusting: boolean) => {
    ctx.save();
    ctx.translate(x, y);
    
    // Tilt based on velocity
    const tilt = Math.max(-0.5, Math.min(0.5, stateRef.current.subV * 0.1));
    ctx.rotate(tilt);

    // Spotlight Cone
    const gradient = ctx.createLinearGradient(0, 0, 300, 0);
    gradient.addColorStop(0, 'rgba(255, 255, 200, 0.4)');
    gradient.addColorStop(1, 'rgba(255, 255, 200, 0)');
    
    ctx.beginPath();
    ctx.moveTo(SUB_WIDTH / 2, 0);
    ctx.lineTo(300, -80);
    ctx.lineTo(300, 80);
    ctx.fillStyle = gradient;
    ctx.fill();

    // Body
    ctx.fillStyle = '#FFD600'; // Bauhaus Yellow
    ctx.beginPath();
    ctx.roundRect(-SUB_WIDTH/2, -SUB_HEIGHT/2, SUB_WIDTH, SUB_HEIGHT, 15);
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#000';
    ctx.stroke();

    // Window
    ctx.fillStyle = '#0044FF'; // Bauhaus Blue
    ctx.beginPath();
    ctx.arc(SUB_WIDTH/4, -5, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Periscope
    ctx.fillStyle = '#FFD600';
    ctx.beginPath();
    ctx.roundRect(-5, -SUB_HEIGHT/2 - 15, 10, 15, 2);
    ctx.fill();
    ctx.stroke();

    // Propeller
    if (isThrusting) {
      ctx.fillStyle = '#FF3333'; // Red active
      ctx.beginPath();
      ctx.arc(-SUB_WIDTH/2 - 5, 0, Math.random() * 8 + 4, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.fillStyle = '#888';
      ctx.beginPath();
      ctx.roundRect(-SUB_WIDTH/2 - 8, -10, 8, 20, 2);
      ctx.fill();
      ctx.stroke();
    }
    
    ctx.restore();
  };

  const gameLoopRef = useRef<() => void>(undefined);
  
  useEffect(() => {
    gameLoopRef.current = () => {
    if (stateRef.current.gameOverTriggered) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const logicalW = BASE_WIDTH;
    const logicalH = BASE_HEIGHT;

    const state = stateRef.current;
    
    // Physics
    if (state.isThrusting) {
      state.subV += THRUST;
    } else {
      state.subV += GRAVITY;
    }
    
    // Terminal velocity
    if (state.subV > MAX_VELOCITY) state.subV = MAX_VELOCITY;
    if (state.subV < -MAX_VELOCITY) state.subV = -MAX_VELOCITY;
    
    state.subY += state.subV;

    // Floor/Ceiling collision
    if (state.subY - SUB_HEIGHT/2 < 0 || state.subY + SUB_HEIGHT/2 > logicalH) {
      gameOver();
      return;
    }

    // Spawn Obstacles
    state.frame++;
    if (state.frame % 10 === 0) {
      state.score += 1; // Passive score over time
      setScore(state.score);
    }

    if (logicalW - state.lastObstacleX > OBSTACLE_SPACING) {
      const gapSize = 200 + Math.random() * 80;
      const minHeight = 50;
      const maxHeight = logicalH - gapSize - minHeight;
      const topHeight = minHeight + Math.random() * (maxHeight - minHeight);
      const bottomHeight = logicalH - topHeight - gapSize;
      
      state.obstacles.push({
        x: logicalW,
        topHeight,
        bottomHeight,
        passed: false
      });
      state.lastObstacleX = logicalW;

      // 30% chance to spawn a pearl in the gap
      if (Math.random() < 0.3) {
        state.pearls.push({
          x: logicalW + OBSTACLE_WIDTH/2,
          y: topHeight + gapSize / 2 + (Math.random() * 40 - 20),
          collected: false
        });
      }
    }

    // Move & Cleanup
    state.obstacles.forEach(obs => obs.x -= OBSTACLE_SPEED);
    state.pearls.forEach(p => p.x -= OBSTACLE_SPEED);
    state.particles.forEach(p => {
      p.x -= p.speed;
      if (p.x < 0) {
        p.x = logicalW;
        p.y = Math.random() * logicalH;
      }
    });

    state.obstacles = state.obstacles.filter(obs => obs.x + OBSTACLE_WIDTH > 0);
    state.pearls = state.pearls.filter(p => p.x + PEARL_RADIUS > 0 && !p.collected);
    state.lastObstacleX -= OBSTACLE_SPEED;

    // Distraction Engine (Expert Mode)
    if (isExpertMode && state.distraction.type === null && state.frame > 300) {
      if (Math.random() < 0.005) { // 0.5% chance per frame once every ~3 seconds
        const types: Array<'squid' | 'jellyfish' | 'sonar' | 'whale'> = ['squid', 'jellyfish', 'sonar', 'whale'];
        state.distraction.type = types[Math.floor(Math.random() * types.length)];
        state.distraction.timer = 0;
        state.distraction.data = {};
        
        // Init specifics
        if (state.distraction.type === 'squid') {
          state.distraction.data.side = Math.random() > 0.5 ? 'bottom' : 'top';
          state.distraction.data.x = logicalW + 100;
        } else if (state.distraction.type === 'jellyfish') {
          state.distraction.data.swarm = Array.from({ length: 15 }, () => ({
            x: logicalW + Math.random() * 200,
            y: Math.random() * logicalH,
            speed: Math.random() * 4 + 4,
            offset: Math.random() * Math.PI * 2
          }));
        } else if (state.distraction.type === 'whale') {
          state.distraction.data.x = logicalW;
          state.distraction.data.y = Math.random() * (logicalH - 300);
        }
      }
    }

    // Drawing — reset transform then scale for DPR and canvas size
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const renderScale = (canvas.width / dpr) / BASE_WIDTH;
    ctx.scale(dpr * renderScale, dpr * renderScale);

    // Background gradient
    const bgGradient = ctx.createLinearGradient(0, 0, 0, logicalH);
    bgGradient.addColorStop(0, '#001133');
    bgGradient.addColorStop(1, '#000000');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, logicalW, logicalH);

    // Whale Distraction (Behind everything)
    if (state.distraction.type === 'whale') {
      const dTimer = state.distraction.timer++;
      const wx = state.distraction.data.x - dTimer * 1.5;
      const wy = state.distraction.data.y;
      
      ctx.fillStyle = '#050505'; // Very dark, blends with background
      ctx.beginPath();
      ctx.ellipse(wx + 300, wy + 100, 300, 100, 0, 0, Math.PI * 2); // Body
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(wx + 600, wy + 100);
      ctx.lineTo(wx + 700, wy + 50);
      ctx.lineTo(wx + 680, wy + 150);
      ctx.fill(); // Tail
      
      if (wx < -800) state.distraction.type = null;
    }

    // Particles
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    state.particles.forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    });

    // Obstacles
    const subLeft = 100 - SUB_WIDTH/2;
    const subRight = 100 + SUB_WIDTH/2;
    const subTop = state.subY - SUB_HEIGHT/2;
    const subBottom = state.subY + SUB_HEIGHT/2;

    state.obstacles.forEach(obs => {
      ctx.fillStyle = '#0a0a0a';
      ctx.strokeStyle = '#0044FF';
      ctx.lineWidth = 4;

      // Draw Top Rock (Stalactite)
      ctx.beginPath();
      ctx.moveTo(obs.x, 0);
      ctx.lineTo(obs.x, obs.topHeight - 30);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH * 0.2, obs.topHeight);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH * 0.5, obs.topHeight - 20);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH * 0.8, obs.topHeight - 5);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH, obs.topHeight - 40);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH, 0);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Top Rock Details (Internal lines)
      ctx.beginPath();
      ctx.moveTo(obs.x + OBSTACLE_WIDTH * 0.3, 0);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH * 0.3, obs.topHeight - 40);
      ctx.moveTo(obs.x + OBSTACLE_WIDTH * 0.7, 0);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH * 0.7, obs.topHeight - 60);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#002288';
      ctx.stroke();

      // Draw Bottom Rock (Stalagmite)
      ctx.fillStyle = '#0a0a0a';
      ctx.strokeStyle = '#0044FF';
      ctx.lineWidth = 4;
      
      const bottomY = logicalH - obs.bottomHeight;
      ctx.beginPath();
      ctx.moveTo(obs.x, logicalH);
      ctx.lineTo(obs.x, bottomY + 40);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH * 0.2, bottomY);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH * 0.5, bottomY + 25);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH * 0.8, bottomY + 10);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH, bottomY + 30);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH, logicalH);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Bottom Rock Details (Internal lines)
      ctx.beginPath();
      ctx.moveTo(obs.x + OBSTACLE_WIDTH * 0.3, logicalH);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH * 0.3, bottomY + 50);
      ctx.moveTo(obs.x + OBSTACLE_WIDTH * 0.7, logicalH);
      ctx.lineTo(obs.x + OBSTACLE_WIDTH * 0.7, bottomY + 30);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#002288';
      ctx.stroke();

      // Collision Detection (Kept simple AABB for arcade fairness)
      const obsLeft = obs.x;
      const obsRight = obs.x + OBSTACLE_WIDTH;
      
      // Simple AABB Collision
      if (
        subRight > obsLeft && 
        subLeft < obsRight && 
        (subTop < obs.topHeight || subBottom > logicalH - obs.bottomHeight)
      ) {
        gameOver();
        return;
      }
    });

    // Pearls
    state.pearls.forEach(p => {
      if (p.collected) return;
      
      // Glow effect
      ctx.shadowColor = '#FFD600';
      ctx.shadowBlur = 10;
      ctx.fillStyle = '#FFF2B2';
      ctx.beginPath();
      ctx.arc(p.x, p.y, PEARL_RADIUS + Math.sin(state.frame * 0.1) * 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Collision
      const dx = p.x - 100; // Sub is always at x=100
      const dy = p.y - state.subY;
      const dist = Math.sqrt(dx*dx + dy*dy);
      
      if (dist < PEARL_RADIUS + Math.max(SUB_WIDTH, SUB_HEIGHT)/2) {
        p.collected = true;
        state.score += 50;
        setScore(state.score);
        playPearlSound();
      }
    });

    // Squid Distraction (In front of rocks, blocks view)
    if (state.distraction.type === 'squid') {
      const dTimer = state.distraction.timer++;
      const side = state.distraction.data.side;
      const sx = state.distraction.data.x - dTimer * 3;
      
      ctx.fillStyle = '#110022'; // Dark purple
      ctx.strokeStyle = '#330066';
      ctx.lineWidth = 5;
      
      ctx.beginPath();
      if (side === 'bottom') {
        const h = Math.min(300, dTimer * 5);
        ctx.moveTo(sx, logicalH);
        ctx.quadraticCurveTo(sx - 50 + Math.sin(dTimer * 0.1) * 30, logicalH - h / 2, sx + 20, logicalH - h);
        ctx.quadraticCurveTo(sx + 50 + Math.sin(dTimer * 0.1) * 30, logicalH - h / 2, sx + 60, logicalH);
      } else {
        const h = Math.min(300, dTimer * 5);
        ctx.moveTo(sx, 0);
        ctx.quadraticCurveTo(sx - 50 + Math.sin(dTimer * 0.1) * 30, h / 2, sx + 20, h);
        ctx.quadraticCurveTo(sx + 50 + Math.sin(dTimer * 0.1) * 30, h / 2, sx + 60, 0);
      }
      ctx.fill();
      ctx.stroke();

      if (sx < -200) state.distraction.type = null;
    }

    // Jellyfish Swarm Distraction
    if (state.distraction.type === 'jellyfish') {
      const dTimer = state.distraction.timer++;
      let active = false;
      state.distraction.data.swarm.forEach((j: any) => {
        j.x -= j.speed;
        j.y += Math.sin(dTimer * 0.1 + j.offset) * 2;
        if (j.x > -50) active = true;

        ctx.fillStyle = 'rgba(255, 50, 200, 0.8)';
        ctx.beginPath();
        ctx.arc(j.x, j.y, 10, Math.PI, 0);
        ctx.lineTo(j.x + 10, j.y + 15);
        ctx.lineTo(j.x - 10, j.y + 15);
        ctx.fill();
      });
      if (!active) state.distraction.type = null;
    }

    // Sonar Flash Distraction (Screen washout)
    if (state.distraction.type === 'sonar') {
      const dTimer = state.distraction.timer++;
      const intensity = Math.max(0, 1 - dTimer / 40); // Fades over 40 frames
      if (intensity > 0) {
        ctx.fillStyle = `rgba(100, 255, 200, ${intensity * 0.7})`;
        ctx.fillRect(0, 0, logicalW, logicalH);
      } else {
        state.distraction.type = null;
      }
    }

    // Draw Sub
    drawSubmarine(ctx, 100, state.subY, state.isThrusting);
    };
  });

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    if (gameState === 'playing') {
      stateRef.current.isThrusting = true;
      playThrustSound();
      setInstructionsVisible(false);
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    e.preventDefault();
    if (gameState === 'playing') {
      stateRef.current.isThrusting = false;
    }
  };

  // ESC key exits fullscreen gracefully
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (isFullscreen || isFakeFullscreen)) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        }
        setIsFakeFullscreen(false);
        setForceRotate(false);
        setIsFullscreen(false);
        tryUnlockOrientation();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen, isFakeFullscreen]);

  useEffect(() => {
    // Initial draw for start screen
    const canvas = canvasRef.current;
    if (canvas && gameState === 'start') {
      // Small delay so resizeCanvas has run
      requestAnimationFrame(() => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const w = BASE_WIDTH;
        const h = BASE_HEIGHT;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const renderScale = (canvas.width / dpr) / BASE_WIDTH;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr * renderScale, dpr * renderScale);
        // Draw static background
        const bgGradient = ctx.createLinearGradient(0, 0, 0, h);
        bgGradient.addColorStop(0, '#001133');
        bgGradient.addColorStop(1, '#000000');
        ctx.fillStyle = bgGradient;
        ctx.fillRect(0, 0, w, h);
        drawSubmarine(ctx, 100, h / 2, false);
      });
    }

    if (gameState === 'playing') {
      const loop = () => {
        gameLoopRef.current?.();
        if (!stateRef.current.gameOverTriggered) {
          reqRef.current = requestAnimationFrame(loop);
        }
      };
      reqRef.current = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(reqRef.current);
      if (gameState === 'gameover' || gameState === 'start') {
        stopBackgroundMusic();
      }
    };
  }, [gameState, isFullscreen, isFakeFullscreen]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div
      ref={wrapperRef}
      className={`
        ${effectiveFullscreen
          ? 'fixed inset-0 z-[9999] bg-black flex items-center justify-center'
          : 'max-w-4xl mx-auto p-4 md:p-8'}
      `}
      style={forceRotate ? {
        /* CSS rotation fallback: rotate the entire wrapper 90° when stuck portrait */
        transform: 'rotate(90deg)',
        transformOrigin: 'center center',
        width: '100vh',
        height: '100vw',
        position: 'fixed',
        top: '50%',
        left: '50%',
        marginTop: 'calc(-50vw)',
        marginLeft: 'calc(-50vh)',
        zIndex: 9999,
      } : undefined}
    >
      {/* Portrait mobile: rotate hint overlay — hidden when forceRotate is active (game is already rotated) */}
      <AnimatePresence>
        {isPortraitMobile && !forceRotate && gameState !== 'gameover' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 bg-black/90 flex flex-col items-center justify-center gap-4 text-white text-center px-6"
          >
            <motion.div
              animate={{ rotate: [0, -90, -90, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
            >
              <RotatePhoneIcon />
            </motion.div>
            <p className="font-mono text-sm tracking-wider uppercase">Rotate device to landscape</p>
            <button
              onClick={async () => {
                // Try orientation lock first
                const locked = await tryLockLandscape();
                if (!locked) {
                  // Can't lock orientation — go fake fullscreen with CSS rotation
                  toggleFullscreen();
                }
              }}
              className="mt-2 border-2 border-white px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-white hover:text-black transition-colors"
            >
              Go fullscreen
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        ref={containerRef}
        className={`
          relative overflow-hidden select-none touch-none bg-black
          ${effectiveFullscreen
            ? 'w-full h-full'
            : 'border-8 border-black dark:border-white rounded-xl shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff]'
          }
        `}
        style={effectiveFullscreen ? undefined : { aspectRatio: '16 / 10' }}
      >
        {/* HUD */}
        <div className="absolute top-4 right-14 z-10 flex gap-4 text-white font-mono font-bold text-xl drop-shadow-md">
          <div>SCORE: {score}</div>
          <div>HI: {highScore}</div>
        </div>

        {/* Top Controls */}
        <div className="absolute top-3 left-3 z-30 flex items-center gap-2">
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded bg-black/60 hover:bg-black/80 text-white border border-white/20 transition-colors"
            title={effectiveFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {effectiveFullscreen ? <CollapseIcon /> : <ExpandIcon />}
          </button>

          <button
            onClick={() => {
              initAudio();
              setMusicTrack(prev => {
                if (prev === 'random') return 'ny_groove';
                if (prev === 'ny_groove') return 'sea_shanty';
                if (prev === 'sea_shanty') return 'mgmt_kids';
                if (prev === 'mgmt_kids') return 'take_a_chance';
                if (prev === 'take_a_chance') return 'knowing_me';
                if (prev === 'knowing_me') return 'off';
                return 'random';
              });
            }}
            className={`p-2 rounded border transition-colors flex items-center gap-1.5 text-xs font-mono font-bold ${
              musicTrack === 'random'
                ? 'bg-purple-950/80 text-purple-300 border-purple-400/60 hover:bg-purple-900/80 shadow-[0_0_10px_rgba(168,85,247,0.3)]'
                : musicTrack === 'ny_groove'
                ? 'bg-cyan-950/80 text-cyan-300 border-cyan-400/60 hover:bg-cyan-900/80'
                : musicTrack === 'sea_shanty'
                ? 'bg-emerald-950/80 text-emerald-300 border-emerald-400/60 hover:bg-emerald-900/80'
                : musicTrack === 'mgmt_kids'
                ? 'bg-fuchsia-950/80 text-fuchsia-300 border-fuchsia-400/60 hover:bg-fuchsia-900/80'
                : musicTrack === 'take_a_chance'
                ? 'bg-red-950/80 text-red-300 border-red-400/60 hover:bg-red-900/80'
                : musicTrack === 'knowing_me'
                ? 'bg-sky-950/80 text-sky-300 border-sky-400/60 hover:bg-sky-900/80'
                : 'bg-black/60 text-white/50 border-white/20 hover:bg-black/80'
            }`}
            title="Cycle music: Random / Shuffle -> NY Groove -> Sea Shanty -> MGMT Kids -> Take a Chance -> Knowing Me -> Mute"
          >
            {musicTrack === 'off' ? <MuteIcon /> : <MusicIcon />}
            <span className="hidden sm:inline">
              {musicTrack === 'random' && `🔀 RANDOM (${TRACK_TITLES[currentRandomTrack]})`}
              {musicTrack === 'ny_groove' && '🎵 NY GROOVE'}
              {musicTrack === 'sea_shanty' && '⚓ SEA SHANTY'}
              {musicTrack === 'mgmt_kids' && '🎸 MGMT KIDS'}
              {musicTrack === 'take_a_chance' && '⚡ TAKE A CHANCE'}
              {musicTrack === 'knowing_me' && '💙 KNOWING ME'}
              {musicTrack === 'off' && 'MUTED'}
            </span>
          </button>
        </div>

        <canvas 
          ref={canvasRef}
          className="cursor-crosshair block mx-auto"
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          style={{ touchAction: 'none' }}
        />

        {gameState === 'playing' && instructionsVisible && (
          <div className="absolute bottom-8 left-0 right-0 pointer-events-none flex justify-center z-10">
            <div className="bg-black/80 text-white font-mono px-6 py-2 rounded-full border border-white/20 animate-pulse">
              Click and hold to thrust up. Release to sink.
            </div>
          </div>
        )}

        {gameState === 'start' && (
          <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center z-20 gap-4">
            <h2 className="text-4xl md:text-6xl text-white font-black uppercase tracking-widest drop-shadow-lg text-center font-mono">Deep Sea Explorer</h2>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                onClick={startGame}
                className="bg-cyan-500 hover:bg-cyan-600 text-black font-black text-lg py-4 px-8 tracking-wider"
              >
                START EXPLORATION
              </Button>
              <Button
                onClick={() => setShowLeaderboard(true)}
                variant="secondary"
                className="bg-black/60 hover:bg-black/80 text-white font-mono font-bold text-lg py-4 px-6 border-2 border-cyan-400"
              >
                🏆 HALL OF FAME
              </Button>
            </div>
            <div className="mt-4 flex items-center gap-3 bg-black/50 p-3 rounded-lg border border-red-500/30">
              <span className="text-red-400 font-mono text-sm uppercase font-bold tracking-widest">EXPERT MODE</span>
              <button
                onClick={() => setIsExpertMode(!isExpertMode)}
                className={`w-12 h-6 rounded-full p-1 transition-colors ${isExpertMode ? 'bg-red-500' : 'bg-gray-600'}`}
              >
                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${isExpertMode ? 'translate-x-6' : 'translate-x-0'}`} />
              </button>
            </div>
            <p className="text-white/80 font-mono text-center text-sm px-4 mt-2">Click or tap and hold to thrust up. Release to sink.</p>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="absolute inset-0 bg-red-950/90 flex flex-col items-center justify-center z-20 p-4">
            <h2 className="text-4xl md:text-6xl text-white font-black uppercase tracking-widest mb-2 font-mono">HULL BREACH</h2>
            <div className="text-2xl text-cyan-300 font-mono mb-4">Final Score: {score}</div>
            
            {isSignedIn ? (
              !scoreSubmitted ? (
                <div className="flex gap-2 max-w-sm w-full mb-6">
                  <input
                    type="text"
                    placeholder="Captain Name..."
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    className="flex-1 bg-black text-white border-2 border-cyan-400 px-3 py-2 text-sm font-mono focus:outline-none"
                  />
                  <Button
                    onClick={() => {
                      if (!playerName.trim()) return toast.error('Enter captain name');
                      submitScoreMutation.mutate({ playerName: playerName.trim(), score });
                    }}
                    disabled={submitScoreMutation.isPending}
                    className="bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase"
                  >
                    {submitScoreMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              ) : (
                <p className="text-emerald-400 font-mono font-bold text-sm mb-6">✓ Score Saved on Leaderboard!</p>
              )
            ) : (
              <div className="bg-black/80 border-2 border-cyan-400/60 p-4 max-w-sm w-full mb-6 text-center shadow-lg">
                <p className="text-xs text-cyan-300 font-mono mb-1">Score saved locally on this device.</p>
                <p className="text-sm font-bold text-white mb-3">Sign in to save your score to the global leaderboard.</p>
                <Link
                  to="/sign-in"
                  className="inline-block w-full py-2.5 bg-cyan-400 hover:bg-cyan-300 text-black font-bold font-mono text-xs uppercase tracking-wider transition-colors"
                >
                  Sign In to Save Score
                </Link>
              </div>
            )}

            <div className="flex gap-3">
              <Button 
                onClick={startGame}
                className="bg-white text-black hover:bg-gray-200 text-lg py-4 px-8 font-black uppercase"
              >
                REDEPLOY VESSEL
              </Button>
              <Button
                onClick={() => setShowLeaderboard(true)}
                variant="secondary"
                className="bg-black/60 text-cyan-300 border-2 border-cyan-400 font-mono font-bold text-lg px-6"
              >
                🏆 Hall of Fame
              </Button>
            </div>
          </div>
        )}

        {/* Leaderboard Modal */}
        <AnimatePresence>
          {showLeaderboard && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-40 bg-black/90 flex flex-col items-center justify-center p-6 text-white font-mono"
            >
              <div className="max-w-md w-full border-4 border-cyan-400 bg-slate-950 p-6 space-y-4 shadow-2xl">
                <div className="flex justify-between items-center border-b-2 border-cyan-400 pb-2">
                  <h3 className="text-xl font-black uppercase text-cyan-300">🏆 Deep Sea Hall of Fame</h3>
                  <button
                    onClick={() => setShowLeaderboard(false)}
                    className="text-white hover:text-cyan-300 font-black text-lg px-2"
                  >
                    ✕
                  </button>
                </div>

                {hasActiveNode && (
                  <GameLeaderboardScopeTabs
                    scope={leaderboardScope}
                    onScopeChange={setLeaderboardScope}
                    nodeName={nodeName}
                  />
                )}

                {isLeaderboardLoading ? (
                  <div className="py-8 text-center text-sm">Loading Rankings...</div>
                ) : leaderboards.length === 0 ? (
                  <div className="py-8 text-center text-xs text-slate-400">No deep sea expeditions recorded yet. Be the first captain!</div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto text-xs">
                    {leaderboards.map((entry, idx) => (
                      <div 
                        key={idx}
                        className={`flex justify-between items-center p-2.5 border border-cyan-400/40 ${
                          idx === 0 ? 'bg-cyan-400 text-black font-black' : 'bg-slate-900 text-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-bold">#{idx + 1}</span>
                          <span className="uppercase font-bold">{entry.playerName}</span>
                        </div>
                        <div className="font-black text-right">
                          {entry.score} pts ({entry.depth || `${entry.score}m`})
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  onClick={() => setShowLeaderboard(false)}
                  className="w-full bg-cyan-400 text-black font-black uppercase"
                >
                  Return to Submarine
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
}
